import { isEfficiencyTier } from './efficiency'
import {
  CPUS,
  findModel,
  GPUS,
  isKeyOf,
  MEMORY_WATTS,
  MONITOR_WATTS,
  OPTICAL_DRIVE_WATTS,
  type PartModel,
  PLATFORM_WATTS,
  STORAGE_WATTS,
} from './parts'
import { estimateLoad } from './power'
import {
  type Build,
  CUSTOM_MODEL,
  DEFAULT_SPEC,
  isCount,
  isValidCustomWatts,
  isValidOtherWatts,
  isValidPrice,
  LIMITS,
  type ModelChoice,
  NO_GPU,
  type PartLine,
  type Spec,
  type Usage,
} from './spec'

/**
 * Share links.
 *
 * Same contract as the other tools: the whole input lives in the URL fragment
 * (`#…`), which browsers never send to a server, and the payload is versioned.
 * Fields are `key=value` pairs so the link stays readable and a field added later
 * does not need a new version — a missing field takes its default:
 *
 *   #v1;pf=desktop;cpu=ryzen-7-9800x3d*1;gpu=rtx-5070*1;mem=ddr5*2;
 *    sto=nvme*1,hdd-35*0;fan=3;oth=0;h=8;g=2;mon=medium*1;eff=gold;kwh=0.2
 *
 * A typed wattage is written as `w<watts>` in place of a model id (`cpu=w125*1`),
 * and integrated graphics as `gpu=none`.
 *
 * Links made before "other components" existed count optical drives instead
 * (`odd=2`). They are still read: each drive becomes its typical draw in other
 * watts, so a bookmarked build restores with the same total.
 */
const VERSION = 'v1'

/** The most optical drives the old picker offered, and so an old link can hold. */
const LEGACY_OPTICAL_DRIVES = 4

export { DEFAULT_SPEC }

/** True when the spec can be calculated, and so is worth putting in a link. */
export function isUsableSpec(spec: Spec): boolean {
  return estimateLoad(spec.build) !== null && isValidPrice(spec.usage.pricePerKwh)
}

export function encodeSpec(spec: Spec): string {
  const { build, usage } = spec
  const fields: [string, string][] = [
    ['pf', build.platform],
    ['cpu', encodeModel(build.cpu)],
    ['gpu', encodeModel(build.gpu)],
    ['mem', encodeLine(build.memory)],
    ['sto', build.storage.map(encodeLine).join(',')],
    ['fan', String(build.fans)],
    ['oth', String(build.otherWatts)],
    ['h', String(usage.hoursPerDay)],
    ['g', String(usage.heavyHoursPerDay)],
    ['mon', encodeLine(usage.monitors)],
    ['eff', usage.efficiency],
    ['kwh', String(usage.pricePerKwh)],
  ]
  return [VERSION, ...fields.map(([key, value]) => `${key}=${value}`)].join(';')
}

function encodeModel(choice: ModelChoice): string {
  if (choice.model === NO_GPU) return NO_GPU
  const model = choice.model === CUSTOM_MODEL ? `w${choice.customWatts}` : choice.model
  return `${model}*${choice.count}`
}

function encodeLine(line: PartLine<string>): string {
  return `${line.type}*${line.count}`
}

/**
 * Returns `null` for anything unrecognised — a bad link must land the visitor on a
 * working calculator, never on an error page, and never on a half-applied build.
 */
export function decodeSpec(payload: string): Spec | null {
  const text = payload.replace(/^#/, '')
  if (text === '') return null

  const [version, ...pairs] = text.split(';')
  if (version !== VERSION) return null

  const fields = new Map<string, string>()
  for (const pair of pairs) {
    const at = pair.indexOf('=')
    if (at <= 0) return null
    fields.set(pair.slice(0, at), pair.slice(at + 1))
  }

  const build = decodeBuild(fields)
  const usage = build && decodeUsage(fields)
  if (!build || !usage) return null
  return { build, usage }
}

/**
 * Each field: absent → the default, present but invalid → `undefined`, which
 * rejects the whole link.
 */
function field<T>(
  fields: Map<string, string>,
  key: string,
  fallback: T,
  parse: (value: string) => T | undefined,
): T | undefined {
  const value = fields.get(key)
  return value === undefined ? fallback : parse(value)
}

function decodeBuild(fields: Map<string, string>): Build | null {
  const fallback = DEFAULT_SPEC.build
  const platform = field(fields, 'pf', fallback.platform, (value) =>
    isKeyOf(PLATFORM_WATTS, value) ? value : undefined,
  )
  const cpu = field(fields, 'cpu', fallback.cpu, (value) =>
    decodeModel(value, CPUS, LIMITS.cpuCount, fallback.cpu, false),
  )
  const gpu = field(fields, 'gpu', fallback.gpu, (value) =>
    decodeModel(value, GPUS, LIMITS.gpuCount, fallback.gpu, true),
  )
  const memory = field(fields, 'mem', fallback.memory, (value) =>
    decodeLine(value, MEMORY_WATTS, LIMITS.memory),
  )
  const storage = field(fields, 'sto', fallback.storage, (value) => {
    const [first, second, ...rest] = value
      .split(',')
      .map((line) => decodeLine(line, STORAGE_WATTS, LIMITS.storage))
    if (!first || !second || rest.length > 0) return undefined
    return [first, second] as const
  })
  const fans = field(fields, 'fan', fallback.fans, (value) => decodeCount(value, LIMITS.fans))
  const typedWatts = field(fields, 'oth', fallback.otherWatts, decodeOtherWatts)
  const legacyOptical = field(fields, 'odd', 0, (value) =>
    decodeCount(value, LEGACY_OPTICAL_DRIVES),
  )
  const otherWatts =
    typedWatts === undefined || legacyOptical === undefined
      ? undefined
      : typedWatts + legacyOptical * OPTICAL_DRIVE_WATTS

  if (
    platform === undefined ||
    cpu === undefined ||
    gpu === undefined ||
    memory === undefined ||
    storage === undefined ||
    fans === undefined ||
    otherWatts === undefined ||
    !isValidOtherWatts(otherWatts)
  ) {
    return null
  }
  return { platform, cpu, gpu, memory, storage, fans, otherWatts }
}

function decodeUsage(fields: Map<string, string>): Usage | null {
  const fallback = DEFAULT_SPEC.usage
  const hoursPerDay = field(fields, 'h', fallback.hoursPerDay, (value) =>
    decodeCount(value, LIMITS.hoursPerDay, LIMITS.minHoursPerDay),
  )
  const heavyHoursPerDay = field(fields, 'g', fallback.heavyHoursPerDay, (value) =>
    decodeCount(value, LIMITS.hoursPerDay),
  )
  const monitors = field(fields, 'mon', fallback.monitors, (value) =>
    decodeLine(value, MONITOR_WATTS, LIMITS.monitors),
  )
  const efficiency = field(fields, 'eff', fallback.efficiency, (value) =>
    isEfficiencyTier(value) ? value : undefined,
  )
  const pricePerKwh = field(fields, 'kwh', fallback.pricePerKwh, (value) => {
    const price = value === '' ? NaN : Number(value)
    return isValidPrice(price) ? price : undefined
  })

  if (
    hoursPerDay === undefined ||
    heavyHoursPerDay === undefined ||
    heavyHoursPerDay > hoursPerDay ||
    monitors === undefined ||
    efficiency === undefined ||
    pricePerKwh === undefined
  ) {
    return null
  }
  return { hoursPerDay, heavyHoursPerDay, monitors, efficiency, pricePerKwh }
}

function decodeOtherWatts(text: string): number | undefined {
  // Plain digits only: Number() would also take '', ' 5', '1e3' and '0x10'.
  if (!/^\d+(\.\d+)?$/.test(text)) return undefined
  const watts = Number(text)
  return isValidOtherWatts(watts) ? watts : undefined
}

function decodeCount(text: string, max: number, min = 0): number | undefined {
  // Number('') is 0, which would turn an empty field into a valid count.
  if (!/^\d+$/.test(text)) return undefined
  const count = Number(text)
  return isCount(count, max, min) ? count : undefined
}

function decodeLine<T extends object>(
  text: string,
  table: T,
  max: number,
): PartLine<Extract<keyof T, string>> | undefined {
  const [type, countText, ...rest] = text.split('*')
  if (rest.length > 0 || countText === undefined || !isKeyOf(table, type)) return undefined
  const count = decodeCount(countText, max)
  if (count === undefined) return undefined
  return { type: type as Extract<keyof T, string>, count }
}

function decodeModel(
  text: string,
  catalogue: readonly PartModel[],
  maxCount: number,
  fallback: ModelChoice,
  allowNone: boolean,
): ModelChoice | undefined {
  if (text === NO_GPU) return allowNone ? { ...fallback, model: NO_GPU, count: 1 } : undefined

  const [model, countText, ...rest] = text.split('*')
  if (model === undefined || countText === undefined || rest.length > 0) return undefined
  const count = decodeCount(countText, maxCount, 1)
  if (count === undefined) return undefined

  const custom = /^w(\d+(?:\.\d+)?)$/.exec(model)
  if (custom) {
    const watts = Number(custom[1])
    return isValidCustomWatts(watts)
      ? { model: CUSTOM_MODEL, customWatts: watts, count }
      : undefined
  }
  return findModel(catalogue, model) ? { ...fallback, model, count } : undefined
}

/** Writes the spec into the fragment without adding a history entry per change. */
export function replaceLocationSpec(spec: Spec): void {
  if (typeof window === 'undefined') return
  const next = `${window.location.pathname}${window.location.search}#${encodeSpec(spec)}`
  window.history.replaceState(null, '', next)
}

export function readLocationSpec(): Spec | null {
  if (typeof window === 'undefined') return null
  return decodeSpec(window.location.hash)
}
